import { Footer } from '@/app/components/Footer'
import { ICategoryWithSubcategoriesHit } from '@/types/search'
import { InitialResults } from 'instantsearch.js'
import Head from 'next/head'
import { useMemo } from 'react'
import { InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch'
import { CollectionSearchParameters } from 'typesense-instantsearch-adapter'
import { SubCategoryCoursesHits } from '../../course/components/SubCategoryCoursesHits'
import { SearchInputProvider } from '../../course/contexts/InputSearchContext'
import { Header } from '../../shared/components/Header'
import { Newsletter } from '../../shared/components/Newsletter'
import { AvailableLanguages } from '../../shared/providers/LanguageSelectionProvider'
import { TaxonomyPageHeader } from '../components/TaxonomyPageHeader'
import { getSearchClient } from '../lib/search-client'

type CategoryPageProps<L extends AvailableLanguages> = {
  category: ICategoryWithSubcategoriesHit
  csrSearchConfig: CollectionSearchParameters
  initialResults?: InitialResults
  locale: L
}

export function CategoryPage<L extends AvailableLanguages>({
  category,
  csrSearchConfig,
  initialResults,
  locale,
  ...props
}: CategoryPageProps<L>) {
  const client = useMemo(
    () =>
      getSearchClient('', {
        collectionSpecificSearchParameters: {
          subcategories_courses: {
            ...csrSearchConfig.subcategories_courses,
            per_page: 1,
          },
        },
      }),
    [csrSearchConfig]
  )

  return (
    <>
      <Head>
        <title>{`${category.name} | Mydra`}</title>
      </Head>
      <SearchInputProvider>
        <Header className="z-40" />
        <TaxonomyPageHeader taxonomy={category} locale={locale} />
        <main {...props} className="container mb-16 mt-12">
          <InstantSearchSSRProvider initialResults={initialResults}>
            <InstantSearch
              indexName="subcategories_courses"
              searchClient={client}
              stalledSearchDelay={500}
              routing={false}
              future={{
                preserveSharedStateOnUnmount: true,
              }}
            >
              <SubCategoryCoursesHits
                className="flex flex-col gap-12"
                infiniteScroll
              />
            </InstantSearch>
          </InstantSearchSSRProvider>
        </main>
        <div className="bg-mydra-black pt-9">
          <Newsletter />
        </div>
        <Footer />
      </SearchInputProvider>
    </>
  )
}
